'use client';

import { Alert, Button, Group, Text, Box } from '@mantine/core';
import { IconVideo, IconClock } from '@tabler/icons-react';
import { useRouter, usePathname } from 'next/navigation';
import dayjs from 'dayjs';
import { useCurrentUser } from '@/providers/CurrentUserProvider';
import { useContactsStore } from '@/stores/useContactsStore';
import { useNotificationStore } from '@/stores/useNotificationStore';

export function UpcomingMeetingBanner() {
  const { user } = useCurrentUser();
  const router = useRouter();
  const pathname = usePathname();
  const { joinMeeting } = useContactsStore();
  const meeting = useNotificationStore(s => s.upcomingMeeting);

  // Hide banner on messages pages
  const isOnMessagesPage = pathname?.includes('/messages');

  const handleJoinMeeting = () => {
    if (!meeting) return;
    
    const otherUserId = meeting.mentorUserId === user?.id ? meeting.menteeUserId : meeting.mentorUserId;
    const isMentor = meeting.mentorUserId === user?.id;
    const prefix = isMentor ? 'mentoring' : 'learning';
    
    // Navigate to messages page
    router.push(`/${prefix}/messages/${otherUserId}`);
    
    // Trigger the join meeting after navigation
    setTimeout(() => {
      joinMeeting({
        id: meeting.id,
        wherebyRoomUrl: meeting.wherebyRoomUrl || '',
        startTime: meeting.startTime,
        endTime: meeting.endTime,
      });
    }, 500);
  };

  const handleViewMeeting = () => {
    if (!meeting) return;
    
    const otherUserId = meeting.mentorUserId === user?.id ? meeting.menteeUserId : meeting.mentorUserId;
    const isMentor = meeting.mentorUserId === user?.id;
    const prefix = isMentor ? 'mentoring' : 'learning';
    
    router.push(`/${prefix}/messages/${otherUserId}`);
  };

  if (!meeting || !user || isOnMessagesPage) {
    return null;
  }

  const startTime = new Date(meeting.startTime);
  const endTime = new Date(meeting.endTime);
  const now = new Date();
  const isHappening = dayjs(now).isAfter(startTime) && dayjs(now).isBefore(endTime);
  const otherPersonName = meeting.mentorUserId === user.id ? meeting.menteeName : meeting.mentorName;

  return (
    <Box
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 999,
      }}
    >
      <Alert
        variant="filled"
        color="blue"
        icon={isHappening ? <IconVideo size={18} /> : <IconClock size={18} />}
        styles={{
          root: {
            borderRadius: 0,
            border: 'none',
          },
          wrapper: {
            alignItems: 'center',
          },
          body: {
            flex: 1,
          },
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <div>
            <Text size="sm" fw={600} c="white">
              {isHappening ? 'Meeting in progress' : 'Upcoming meeting'} {otherPersonName && `with ${otherPersonName}`}
            </Text>
            <Text size="xs" c="white" opacity={0.9}>
              {dayjs(startTime).format('h:mm A')} - {dayjs(endTime).format('h:mm A')}
            </Text>
          </div>

          <Group gap="xs">
            <Button
              size="xs"
              variant="white"
              onClick={handleJoinMeeting}
            >
              Join Meeting
            </Button>
            <Button
              size="xs"
              variant="subtle"
              c="white"
              onClick={handleViewMeeting}
            >
              View Chat
            </Button>
          </Group>
        </Group>
      </Alert>
    </Box>
  );
}
